import timeIcon from '@/assets/icon-home/time.png';
import { ThemeSwitch } from '@/components/shared/ThemeSwitch';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PATHS } from '@/constants';
import { usePermittedReports } from '@/features/landing/hooks/useReports';
import { useThemePreference } from '@/hooks/useThemePreference';
import { cn } from '@/lib/utils';
import { useThemeStore } from '@/store/themeStore';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

interface ReportPage {
  id: string;
  pageName: string;
  description?: string;
  refPageId: string;
}

interface Report {
  id: string;
  reportName: string;
  description?: string;
  icon?: string;
  pages?: ReportPage[];
}

interface ReportCategory {
  id: string;
  categoryName: string;
  description?: string;
  icon?: string;
  reports?: Report[];
}

function SidebarTooltip({
  name,
  description,
  children,
}: {
  name: string;
  description?: string;
  children: React.ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={12}
        className="max-w-[280px] py-2"
      >
        <p className="text-sm font-semibold">{name}</p>
        {description && (
          <p className="text-muted-foreground mt-1 text-xs">{description}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export interface PortalSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  onSelect?: () => void;
}

const SidebarPageItem = memo(function SidebarPageItem({
  page,
  refPageId,
  reportId,
  isActive,
  onSelect,
}: {
  page: ReportPage;
  reportId: string;
  refPageId: string;
  isActive: boolean;
  onSelect?: () => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('moduleId');

  const handleClick = useCallback(() => {
    navigate(
      `${PATHS.REPORT_VIEW}?reportId=${reportId}&pageId=${page.id}&moduleId=${moduleId}&refPageId=${refPageId}`,
    );
    onSelect?.();
  }, [navigate, reportId, page.id, moduleId, onSelect]);

  return (
    <SidebarTooltip
      name={page.pageName}
      description={page.description}
    >
      <div className="group flex w-full justify-start overflow-hidden pl-[18px]">
        <Button
          variant="ghost"
          onClick={handleClick}
          className={cn(
            'h-[24px] w-full justify-start gap-2 py-2 pr-1 pl-2 font-normal transition-all duration-200 ease-out',
            'hover:bg-primary/10 rounded-l-[4px] rounded-r-none border-l-[3px] border-l-transparent hover:shadow-sm',
            isActive &&
              'bg-primary/20 text-foreground border-l-primary hover:bg-primary/25 font-medium shadow-[0_1px_4px_0_hsl(var(--foreground)/0.1)]',
          )}
        >
          <span className="truncate">{page.pageName}</span>
        </Button>
      </div>
    </SidebarTooltip>
  );
});

const SidebarReportGroup = memo(function SidebarReportGroup({
  report,
  isSearching,
  onSelect,
}: {
  report: Report;
  isSearching?: boolean;
  onSelect?: () => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPageId = searchParams.get('pageId');
  const moduleId = searchParams.get('moduleId');

  const hasActivePage = useMemo(() => {
    return report.pages?.some(p => p.id === currentPageId);
  }, [report.pages, currentPageId]);

  const [isOpen, setIsOpen] = useState(hasActivePage);

  useEffect(() => {
    if (isSearching) {
      setIsOpen(true);
    } else {
      setIsOpen(hasActivePage);
    }
  }, [hasActivePage, isSearching]);

  const handleReportClick = useCallback(() => {
    const firstPage = report.pages?.[0];
    if (firstPage) {
      navigate(
        `${PATHS.REPORT_VIEW}?reportId=${report.id}&pageId=${firstPage.id}&moduleId=${moduleId}&refPageId=${firstPage.refPageId}`,
      );
      onSelect?.();
    }
    setIsOpen(prev => !prev);
  }, [navigate, report, moduleId, onSelect]);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="mb-3 flex w-full flex-col overflow-hidden px-[6px]"
    >
      <SidebarTooltip
        name={report.reportName}
        description={report.description}
      >
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            onClick={handleReportClick}
            className={cn(
              'hover:bg-primary/10 h-[30px] w-full justify-between rounded-[20px] border border-transparent pr-1.5 pl-2 transition-all duration-200 ease-out hover:shadow-sm',
              hasActivePage &&
                'bg-primary/20 text-foreground border-primary-foreground hover:bg-primary/25 font-medium shadow-[0_1px_4px_0_hsl(var(--foreground)/0.15)]',
            )}
          >
            <div className="flex-start text-foreground min-w-0 gap-1.5 overflow-hidden">
              <img
                src={report.icon || timeIcon}
                alt={report.reportName}
                className="h-4 w-4 shrink-0"
              />
              <span className="truncate font-medium">{report.reportName}</span>
            </div>
            <ChevronDown
              size={14}
              className={cn(
                'shrink-0 transition-transform duration-300 ease-out',
                hasActivePage ? 'text-primary' : 'text-foreground',
                isOpen && 'rotate-180',
              )}
            />
          </Button>
        </CollapsibleTrigger>
      </SidebarTooltip>

      <CollapsibleContent className="animate-in fade-in-0 slide-in-from-top-1 mt-2 flex w-full flex-col gap-2 overflow-hidden duration-200">
        {report.pages?.map(page => (
          <SidebarPageItem
            key={page.id}
            page={page}
            refPageId={page.refPageId}
            reportId={report.id}
            isActive={currentPageId === page.id}
            onSelect={onSelect}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
});

const SidebarAppGroup = memo(function SidebarAppGroup({
  category,
  isExpanded,
  isOpen,
  onOpenChange,
  isSearching,
  onSelect,
}: {
  category: ReportCategory;
  isExpanded: boolean;
  isOpen: boolean;
  onOpenChange: (categoryId: string, open: boolean) => void;
  isSearching?: boolean;
  onSelect?: () => void;
}) {
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('moduleId');
  const currentReportId = searchParams.get('reportId');
  const currentPageId = searchParams.get('pageId');

  const hasActiveReport = useMemo(() => {
    return category.reports?.some(r => r.id === currentReportId);
  }, [category.reports, currentReportId]);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const navigate = useNavigate();

  const handlePageClick = useCallback(
    (reportId: string, pageId: string, refPageId?: string) => {
      navigate(
        `${PATHS.REPORT_VIEW}?reportId=${reportId}&pageId=${pageId}&moduleId=${moduleId}${refPageId ? `&refPageId=${refPageId}` : ''}`,
      );
      setPopoverOpen(false);
      onSelect?.();
    },
    [navigate, moduleId, onSelect],
  );

  const handleToggle = useCallback(
    (open: boolean) => {
      onOpenChange(category.id, open);
    },
    [category.id, onOpenChange],
  );

  const collapsedPopoverContent = useMemo(
    () => (
      <div className="flex flex-col gap-1">
        <div className="border-border mb-2 border-b pb-2">
          <span className="text-primary text-sm font-semibold uppercase">
            {category.categoryName}
          </span>
          {category.description && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              {category.description}
            </p>
          )}
        </div>
        {category.reports?.map(report => (
          <div
            key={report.id}
            className="flex flex-col"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-start text-foreground gap-2 px-1 py-1 text-sm font-medium">
                  <img
                    src={report.icon || timeIcon}
                    alt={report.reportName}
                    className="h-4 w-4 shrink-0"
                  />
                  <span className="truncate">{report.reportName}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={12}
                className="max-w-[280px] py-2"
              >
                <p className="text-sm font-semibold">{report.reportName}</p>
                {report.description && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    {report.description}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
            <div className="ml-[15px] flex flex-col gap-0.5">
              {report.pages?.map(page => (
                <Tooltip key={page.id}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        handlePageClick(report.id, page.id, page.refPageId);
                        onSelect?.();
                      }}
                      className={cn(
                        'h-auto justify-start gap-1 px-2 py-1 text-xs font-normal',
                        currentPageId === page.id
                          ? 'bg-primary/15 text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-primary/10',
                      )}
                    >
                      <ChevronRight
                        size={12}
                        className="shrink-0"
                      />
                      <span className="truncate">{page.pageName}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    sideOffset={12}
                    className="max-w-[280px] py-2"
                  >
                    <p className="text-sm font-semibold">{page.pageName}</p>
                    {page.description && (
                      <p className="text-muted-foreground mt-1 text-xs">
                        {page.description}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        ))}
      </div>
    ),
    [category, currentPageId, handlePageClick],
  );

  if (isExpanded) {
    return (
      <Collapsible
        open={isOpen}
        onOpenChange={handleToggle}
        className="mb-2 flex w-full flex-col overflow-hidden rounded-md"
      >
        <SidebarTooltip
          name={category.categoryName}
          description={category.description}
        >
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'flex h-[38px] w-full justify-between rounded-none pr-1.5 pl-2 transition-colors',
                isOpen
                  ? 'bg-primary hover:bg-primary/90 text-white'
                  : hasActiveReport
                    ? 'bg-primary/10 text-primary border-l-primary hover:bg-primary/15 border-l-[3px] font-semibold'
                    : 'bg-card text-foreground hover:bg-accent',
              )}
            >
              <div className="flex-start min-w-0 gap-2">
                <div className="flex-center bg-background h-6 w-6 shrink-0 rounded-full">
                  <img
                    src={category.icon || timeIcon}
                    alt={category.categoryName}
                    className="h-[16px] w-[16px]"
                  />
                </div>
                <span className="text-md truncate font-semibold tracking-wide uppercase">
                  {category.categoryName}
                </span>
              </div>
              <ChevronDown
                size={18}
                className={cn(
                  'shrink-0 transition-transform duration-300 ease-out',
                  isOpen && 'rotate-180',
                )}
              />
            </Button>
          </CollapsibleTrigger>
        </SidebarTooltip>

        <CollapsibleContent
          className="animate-in fade-in-0 slide-in-from-top-2 flex w-full flex-col overflow-hidden pt-3 duration-300"
          style={{ backgroundColor: 'var(--custom-sidebar-bg)' }}
        >
          {category.reports?.map(report => (
            <SidebarReportGroup
              key={report.id}
              report={report}
              isSearching={isSearching}
              onSelect={onSelect}
            />
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="mb-3 flex flex-col items-center">
      <Tooltip open={popoverOpen ? false : undefined}>
        <TooltipTrigger asChild>
          <div>
            <Popover
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-9 w-9 rounded-full transition-all hover:scale-105 focus-visible:ring-0 focus-visible:ring-offset-0',
                    hasActiveReport
                      ? 'bg-primary hover:bg-primary shadow-md'
                      : 'bg-card hover:bg-primary/10',
                  )}
                >
                  <div className="flex-center bg-background h-8 w-8 rounded-full">
                    <img
                      src={category.icon || timeIcon}
                      alt={category.categoryName}
                      className="h-5 w-5"
                    />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="right"
                align="start"
                sideOffset={8}
                className="max-h-[70vh] w-64 overflow-y-auto p-3"
              >
                {collapsedPopoverContent}
              </PopoverContent>
            </Popover>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={12}
          className="max-w-[280px] py-2"
        >
          <p className="text-sm font-semibold">{category.categoryName}</p>
          {category.description && (
            <p className="text-muted-foreground mt-1 text-xs">
              {category.description}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </div>
  );
});

export const PortalSidebar = memo(function PortalSidebar({
  isExpanded,
  onToggle,
  onSelect: onSelectProp,
}: PortalSidebarProps) {
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('moduleId');
  const { theme: configTheme } = useThemeStore();
  const { t } = useTranslation();

  const { data: reportCategories, isLoading } = usePermittedReports(
    moduleId || '',
  );


  const [search, setSearch] = useState('');
  const [openCategoryIds, setOpenCategoryIds] = useState<Set<string>>(
    new Set(),
  );
  const deferredSearch = useDeferredValue(search);
  const { updateTheme, currentTheme } = useThemePreference();

  const currentReportId = searchParams.get('reportId');
  // Sync open categories with search or active report
  useEffect(() => {
    if (!reportCategories) return;

    if (deferredSearch.trim()) {
      const query = deferredSearch.toLowerCase().trim();
      const next = new Set<string>();

      // Maintain active category visibility
      if (currentReportId) {
        const activeCatId = reportCategories.find((cat: ReportCategory) =>
          cat.reports?.some(r => r.id === currentReportId),
        )?.id;
        if (activeCatId) next.add(activeCatId);
      }

      reportCategories.forEach((cat: ReportCategory) => {
        const hasMatch =
          cat.categoryName.toLowerCase().includes(query) ||
          cat.reports?.some(
            rep =>
              rep.reportName.toLowerCase().includes(query) ||
              rep.pages?.some(p => p.pageName.toLowerCase().includes(query)),
          );
        if (hasMatch) {
          next.add(cat.id);
        }
      });
      setOpenCategoryIds(next);
    } else {
      // RESET: When search is cleared, only keep the active category open
      const activeCatId = currentReportId
        ? reportCategories.find((cat: ReportCategory) =>
            cat.reports?.some(r => r.id === currentReportId),
          )?.id
        : null;

      if (activeCatId) {
        setOpenCategoryIds(new Set([activeCatId]));
      } else {
        setOpenCategoryIds(new Set());
      }
    }
  }, [deferredSearch, reportCategories, currentReportId]);

  const handleSelect = useCallback(() => {
    setSearch('');
    onSelectProp?.();
  }, [onSelectProp]);

  const handleCategoryOpenChange = useCallback(
    (categoryId: string, open: boolean) => {
      setOpenCategoryIds(prev => {
        const next = new Set(prev);
        if (open) {
          next.add(categoryId);
        } else {
          next.delete(categoryId);
        }
        return next;
      });
    },
    [],
  );

  const isLightMode = currentTheme !== 'dark';

  const handleThemeToggle = useCallback(() => {
    updateTheme(isLightMode ? 'dark' : 'light');
  }, [isLightMode, updateTheme]);

  const filteredCategories = useMemo(() => {
    if (!reportCategories || !deferredSearch.trim()) return reportCategories;

    const query = deferredSearch.toLowerCase().trim();
    return reportCategories
      .map((category: ReportCategory) => {
        const categoryNameMatches = category.categoryName
          .toLowerCase()
          .includes(query);

        // L1 match: show full category with all reports + pages
        if (categoryNameMatches) {
          return category;
        }

        // Check L2 and L3 matches
        const matchedReports = category.reports
          ?.map(report => {
            const reportNameMatches = report.reportName
              .toLowerCase()
              .includes(query);

            // L2 match: show report with all its pages
            if (reportNameMatches) {
              return report;
            }

            // L3 match: show only matching pages
            const matchedPages = report.pages?.filter(page =>
              page.pageName.toLowerCase().includes(query),
            );

            if (matchedPages && matchedPages.length > 0) {
              return { ...report, pages: matchedPages };
            }

            return null;
          })
          .filter(Boolean) as Report[] | undefined;

        if (matchedReports && matchedReports.length > 0) {
          return { ...category, reports: matchedReports };
        }

        return null;
      })
      .filter(Boolean) as ReportCategory[];
  }, [reportCategories, deferredSearch]);

  return (
    <div
      className={cn(
        'font-inter bg-background flex h-full flex-col overflow-hidden transition-all duration-300 ease-in-out',
        isExpanded ? 'w-[260px]' : 'w-[80px]',
      )}
      style={{ boxShadow: '0px 4px 20px 0px #00000040' }}
    >
      <div
        className={cn(
          'flex h-[56px] shrink-0 items-center',
          isExpanded ? 'justify-between px-3' : 'justify-center px-2',
        )}
      >
        {isExpanded ? (
          <Link
            to="/"
            className="flex-start"
          >
            <img
              src={configTheme?.images.logoMenu}
              alt="Gas South"
              className="h-[49px] object-contain transition-all duration-300"
            />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 transition-opacity hover:opacity-80"
            aria-label="Expand sidebar"
          >
            <img
              src={configTheme?.images.logoMini}
              alt="Gas South"
              className="h-11 w-11 object-contain transition-all duration-300"
            />
          </button>
        )}
        {isExpanded && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="group hover:bg-primary/10 h-9 w-9"
          >
            <span className="material-symbols-rounded text-primary -scale-x-100 text-[22px]">
              sort
            </span>
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="px-[6px] pt-3">
          <div className="relative w-full">
            <Search
              className="text-muted-foreground absolute top-1/2 left-2 z-10 -translate-y-1/2"
              size={16}
            />
            <Input
              placeholder={t('common.search')}
              className="h-[36px] w-full pl-7"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      )}

      <div
        className={cn(
          'scrollbar-hide flex-1 overflow-y-auto py-2',
          isExpanded ? 'px-[6px]' : 'px-2',
        )}
      >
        {isLoading ? (
          <div
            className={cn(
              'flex flex-col gap-4',
              isExpanded ? 'p-4' : 'items-center p-2',
            )}
          >
            {[1, 2, 3].map(i => (
              <Skeleton
                key={i}
                className={cn(
                  isExpanded ? 'h-10 w-full' : 'h-10 w-10 rounded-full',
                )}
              />
            ))}
          </div>
        ) : (
          filteredCategories?.map((category: ReportCategory) => (
            <SidebarAppGroup
              key={category.id}
              category={category}
              isExpanded={isExpanded}
              isOpen={openCategoryIds.has(category.id)}
              onOpenChange={handleCategoryOpenChange}
              isSearching={!!deferredSearch.trim()}
              onSelect={handleSelect}
            />
          ))
        )}
      </div>

      <div className={cn('shrink-0', isExpanded ? 'p-5' : 'flex-center p-3')}>
        <ThemeSwitch
          isLightMode={isLightMode}
          onToggle={handleThemeToggle}
          isExpanded={isExpanded}
        />
      </div>
    </div>
  );
});
